import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  LOOM_PRIMITIVE_CATALOG,
  createDefaultLoomPrimitiveNode,
  loomMirrorAeroGate,
  parseLoomAuthoringCommand,
  type LoomAxis,
  type LoomAuthoringCommand,
  type LoomAuthoringDocument,
  type LoomMirrorPartnerAttach,
  type LoomPrimitiveDefinition,
  type LoomSceneNode,
} from "./loomAuthoringModel";
import { deriveLoomMirrorAttributes } from "./loomMirrorRelations";
import { LoomAuthoringChat } from "./LoomAuthoringChat";
import { LoomAutoParameterControls } from "./LoomAutoParameterControls";
import { LoomExplicitSurfaceCage } from "./LoomExplicitSurfaceCage";
import { LoomOperationHistory } from "./LoomOperationHistory";
import { LoomPatternPlaybookPanel } from "./LoomPatternPlaybookPanel";
import { LoomPatternTree } from "./LoomPatternTree";
import { LoomProductTree } from "./LoomProductTree";
import { LoomActivePartActions } from "./LoomActivePartActions";
import { LoomAirshowPresetBrowser } from "./LoomAirshowPresetBrowser";
import { LoomControlSurfacePanel } from "./LoomControlSurfacePanel";
import {
  createLoomCommandId,
  type LoomAuthoringHistory,
  type LoomCommandAcceptance,
} from "./loomAuthoringHistory";
import {
  LOOM_GOAL_CAPABILITY,
  LOOM_OBSERVABLES,
  pinnedLoomControlIds,
  type LoomControlWorkspace,
  type LoomControlWorkspaceContext,
  type LoomControlWorkspaceIntent,
} from "./loomControlWorkspace";
import type { LoomControlWorkspaceHistory } from "./loomControlWorkspaceHistory";
import { deriveLoomParameterControls } from "./loomParameterControls";
import type { LoomAuthoringPreviewRequest, LoomPersistenceWriteState } from "./useLoomAuthoringHistory";
import type { LoomHistoryStorageStatus } from "./loomAuthoringHistoryStorage";
import "./loom-authoring.css";
export type LoomDefinitionRecordState =
  | { status: "unavailable" }
  | { status: "idle" }
  | { status: "recording" }
  | { status: "recorded"; artifactVersionId: string; replayed: boolean }
  | { status: "error"; message: string };
interface LoomPrimitiveAuthoringPanelProps {
  assistantProjectId: string;
  authoringReadOnlyReason: string | null;
  document: LoomAuthoringDocument;
  extractionDirty: boolean;
  history: LoomAuthoringHistory;
  hideProductTree?: boolean;
  hideTaskDuplicateControls?: boolean;
  inspection: ReactNode;
  lastCommand: LoomAuthoringCommand | null;
  onCommand: (command: LoomAuthoringCommand, acceptance?: LoomCommandAcceptance) => string | null;
  onPreviewCancel: () => void;
  onPreviewCommand: (command: LoomAuthoringCommand, request: LoomAuthoringPreviewRequest) => string | null;
  onRedo: () => void;
  onRecordDefinition: () => void;
  onSelect: (nodeId: string) => void;
  onUndo: () => void;
  onWorkspaceIntent: (intent: LoomControlWorkspaceIntent, context: LoomControlWorkspaceContext) => string | null;
  onWorkspaceRedo: () => void;
  onWorkspaceUndo: () => void;
  persistenceWriteState: LoomPersistenceWriteState;
  recordState: LoomDefinitionRecordState;
  selectedNodeId: string;
  shellView?: "pattern";
  storageStatus: LoomHistoryStorageStatus;
  workspace: LoomControlWorkspace;
  workspaceHistory: LoomControlWorkspaceHistory;
}
function commandBase(document: LoomAuthoringDocument, suffix: string) { return {
  schema: "loom-authoring-command/v1" as const, commandId: createLoomCommandId(`gui-${suffix}`, document.revision), expectedRevision: document.revision,
}; }
function nodeInputs(node: LoomSceneNode): readonly string[] {
  if (node.type === "transform") return [node.inputNodeId];
  if (node.type === "boolean") return node.inputNodeIds;
  return [];
}
function availableNodeId(document: LoomAuthoringDocument, preferred: string): string {
  if (!document.nodes.some((node) => node.id === preferred)) return preferred;
  let suffix = 2;
  while (document.nodes.some((node) => node.id === `${preferred}-${suffix}`)) suffix += 1;
  return `${preferred}-${suffix}`;
}
export function LoomPrimitiveAuthoringPanel({
  assistantProjectId,
  authoringReadOnlyReason,
  document,
  extractionDirty,
  history,
  hideProductTree = false,
  hideTaskDuplicateControls = false,
  inspection,
  lastCommand,
  onCommand,
  onPreviewCancel,
  onPreviewCommand,
  onRedo,
  onRecordDefinition,
  onSelect,
  onUndo,
  onWorkspaceIntent,
  onWorkspaceRedo,
  onWorkspaceUndo,
  persistenceWriteState,
  recordState,
  selectedNodeId,
  shellView,
  storageStatus,
  workspace,
  workspaceHistory,
}: LoomPrimitiveAuthoringPanelProps) {
  const [tab, setTab] = useState<"build" | "inspect" | "command">("build");
  const [primitive, setPrimitive] = useState<LoomPrimitiveDefinition["primitive"]>("sphere");
  const [mirrorAxis, setMirrorAxis] = useState<LoomAxis>("y");
  const [mirrorPlaneOffsetM, setMirrorPlaneOffsetM] = useState(0);
  const [commandText, setCommandText] = useState("");
  const [commandError, setCommandError] = useState<string | null>(null);
  const rawSelected = document.nodes.find((node) => node.id === selectedNodeId) ?? document.nodes[0] ?? null;
  const selected = rawSelected;
  const effectiveSelectedNodeId = selected?.id ?? selectedNodeId;
  const selectedControls = useMemo(() => deriveLoomParameterControls(document, effectiveSelectedNodeId), [document, effectiveSelectedNodeId]);
  const pinnedControlIds = pinnedLoomControlIds(workspace, effectiveSelectedNodeId);
  const visibleGoal = workspace.goal && selectedControls.some((control) => control.id === workspace.goal?.variableControlId)
    ? workspace.goal
    : null;
  const rootedNodeIds = useMemo(() => {
    const byId = new Map(document.nodes.map((node) => [node.id, node])); const reached = new Set<string>();
    const visit = (id: string) => { if (reached.has(id)) return; reached.add(id); const node = byId.get(id); if (node) nodeInputs(node).forEach(visit); };
    document.rootNodeIds.forEach(visit); return reached;
  }, [document]);
  const mirrorAttributes = useMemo(() => deriveLoomMirrorAttributes(document), [document]);
  const selectedDerivedMirror = selected
    ? mirrorAttributes.find((attribute) => attribute.partnerNodeId === selected.id) ?? null
    : null;
  const linkedMirrorAttributes = selected ? mirrorAttributes.filter((attribute) => attribute.sourceNodeId === selected.id) : [];
  const mirrorAttribute = linkedMirrorAttributes.length === 1 ? linkedMirrorAttributes[0]! : null;
  const mirrorPartner = mirrorAttribute
    ? document.nodes.find((node) => node.id === mirrorAttribute.partnerNodeId && node.type === "transform") ?? null
    : null;
  const attachmentUnions = selected ? document.nodes.filter((node) => (
    node.type === "boolean" && node.operation === "union" && rootedNodeIds.has(node.id) && node.inputNodeIds.includes(selected.id)
  )) : [];
  const mirrorAttachment: LoomMirrorPartnerAttach | null = !selected ? null : attachmentUnions.length === 1
    ? { kind: "existing-union", booleanNodeId: attachmentUnions[0]!.id }
    : attachmentUnions.length === 0 && document.rootNodeIds.includes(selected.id)
      ? { kind: "replace-root", unionNodeId: `mirror-union-${document.revision + 1}`, unionLabel: `${selected.label} pair` }
      : null;
  useEffect(() => { if (lastCommand) setCommandText(JSON.stringify(lastCommand, null, 2)); }, [lastCommand]);
  useEffect(() => {
    if (shellView === "pattern") setTab("build");
  }, [shellView]);
  useEffect(() => {
    setMirrorAxis(mirrorAttribute?.axis ?? "y");
    setMirrorPlaneOffsetM(mirrorAttribute?.planeOffsetM ?? 0);
  }, [effectiveSelectedNodeId, mirrorAttribute?.axis, mirrorAttribute?.planeOffsetM]);
  const submit = (command: LoomAuthoringCommand, subjectIds: readonly string[] = []) => {
    const error = onCommand(command, { origin: "gui.scene", subjectIds });
    setCommandError(error);
    return error;
  };
  const applyWorkspaceIntent = (intent: LoomControlWorkspaceIntent): string | null => {
    const error = onWorkspaceIntent(intent, {
      selectedNodeId: effectiveSelectedNodeId,
      controls: selectedControls,
      observables: LOOM_OBSERVABLES,
      solverCapability: LOOM_GOAL_CAPABILITY,
    });
    if (!error && intent.kind === "controls.pin") setTab("build");
    return error;
  };
  const addPrimitive = () => {
    const id = `${primitive}-${document.revision + 1}`;
    const error = submit({ ...commandBase(document, "add"), kind: "node.attach", node: createDefaultLoomPrimitiveNode(id, primitive), attachment: { kind: "new-root" } }, [id]);
    if (!error) onSelect(id);
  };
  const setWingMirrorEnabled = (enabled: boolean) => {
    if (!selected || selected.type !== "primitive" || selected.definition.primitive !== "wing") return;
    if (!enabled && mirrorAttribute) {
      submit({
        ...commandBase(document, "wing-mirror-off"), kind: "mirror.partner.remove",
        sourceNodeId: selected.id, partnerNodeId: mirrorAttribute.partnerNodeId,
      }, [selected.id, mirrorAttribute.partnerNodeId]);
      return;
    }
    if (!enabled || mirrorAttribute || !mirrorAttachment) return;
    const partnerNodeId = availableNodeId(document, `${selected.id}-mirror`);
    const attachmentId = mirrorAttachment.kind === "existing-union" ? mirrorAttachment.booleanNodeId : mirrorAttachment.unionNodeId;
    submit({
      ...commandBase(document, "wing-mirror-on"), kind: "mirror.partner.create", sourceNodeId: selected.id,
      partner: { nodeId: partnerNodeId, label: `${selected.label} reflected surface` },
      plane: { frame: "world", axis: mirrorAxis, offsetM: mirrorPlaneOffsetM }, attach: mirrorAttachment,
    }, [selected.id, partnerNodeId, attachmentId]);
  };
  const updateWingMirror = (axis: LoomAxis, planeOffsetM: number) => {
    if (!selected || !mirrorPartner || mirrorPartner.type !== "transform" || mirrorPartner.operation.transform !== "mirror" || !Number.isFinite(planeOffsetM)) return;
    submit({
      ...commandBase(document, "wing-mirror-plane"), kind: "transform.replace",
      nodeId: mirrorPartner.id, inputNodeId: selected.id,
      operation: { transform: "mirror", axis, planeOffsetM },
    }, [selected.id, mirrorPartner.id]);
  };
  return (
    <>
      <div className="pg-panel-heading">
        <div>
          <span>Native definition</span>
          <strong>Native wing/body Pattern · definition r{document.revision}</strong>
        </div>
        <span className={`pg-live ${extractionDirty ? "is-running" : "is-solved"}`} data-testid="loom-extraction-state">
          <i />{extractionDirty ? "aircraft update pending" : "geometry current"}
        </span>
      </div>
      {!shellView ? <LoomOperationHistory history={history} onRedo={onRedo} onUndo={onUndo} onWorkspaceRedo={onWorkspaceRedo} onWorkspaceUndo={onWorkspaceUndo} persistenceWriteState={persistenceWriteState} storageStatus={storageStatus} workspaceHistory={workspaceHistory} /> : null}
      {authoringReadOnlyReason ? <div className="pg-inline-error" data-testid="loom-authoring-read-only" role="status">{authoringReadOnlyReason}</div> : null}
      {!shellView ? <div className="loom-authoring-tabs" role="tablist" aria-label="Loom definition tools">
        {(["build", "inspect", "command"] as const).map((value) => (
          <button
            aria-selected={tab === value}
            key={value}
            onClick={() => setTab(value)}
            role="tab"
            type="button"
          >{value === "command" ? "API / LLM" : value}</button>
        ))}
      </div> : <div className="loom-pattern-command-entry">
        <button
          aria-pressed={tab === "command"}
          data-testid="loom-pattern-command-toggle"
          onClick={() => setTab((current) => current === "command" ? "build" : "command")}
          type="button"
        >{tab === "command" ? "Back to Pattern" : "API / LLM workspace"}</button>
      </div>}
      {tab === "inspect" ? inspection : tab === "command" ? (
        <div aria-disabled={Boolean(authoringReadOnlyReason)} className="loom-command-panel" data-testid="loom-command-panel" inert={authoringReadOnlyReason ? true : undefined}>
          <LoomAuthoringChat
            activeStateId={history.activeHead.stateId}
            document={document}
            goal={visibleGoal}
            onCommand={onCommand}
            onWorkspaceIntent={applyWorkspaceIntent}
            observables={LOOM_OBSERVABLES}
            projectId={assistantProjectId}
            selectedNodeId={effectiveSelectedNodeId}
          />
          <details className="loom-command-advanced">
            <summary>Advanced API / command JSON</summary>
            <div>
              <p>GUI and direct SDK commands share the validated <code>LoomAuthoringCommand</code> reducer. The local copilot handles allowlisted schema controls; structural mirror edits are currently GUI/API only.</p>
              <code>Record resulting definition: POST /api/thread/v1/aero/parametric-geometries</code>
              <textarea aria-label="Loom authoring command JSON" onChange={(event) => setCommandText(event.target.value)} spellCheck={false} value={commandText} />
              <button type="button" onClick={() => {
                try {
                  const parsed = parseLoomAuthoringCommand(JSON.parse(commandText));
                  const error = onCommand(parsed, { origin: "api.command" });
                  setCommandError(error);
                } catch (error) {
                  setCommandError(error instanceof Error ? error.message : "Invalid Loom command");
                }
              }}>Apply command</button>
              {commandError ? <div className="pg-inline-error" role="alert">{commandError}</div> : null}
              <small>The recording endpoint stores the resulting definition, not this individual command. Definition changes mark extraction stale; rename/reorder do not.</small>
            </div>
          </details>
          <div className="loom-record-definition" data-testid="loom-record-definition-state">
            <button
              aria-busy={recordState.status === "recording"}
              data-testid="loom-record-definition"
              disabled={recordState.status === "unavailable" || recordState.status === "recording" || Boolean(authoringReadOnlyReason)}
              onClick={onRecordDefinition}
              type="button"
            >Record resulting definition</button>
            {authoringReadOnlyReason ? (
              <small className="is-error" role="alert">Recording disabled: {authoringReadOnlyReason}</small>
            ) : recordState.status === "unavailable" ? (
              <small>Persist the project before recording V3 definition</small>
            ) : recordState.status === "recording" ? (
              <small role="status">Recording definition r{document.revision}…</small>
            ) : recordState.status === "recorded" ? (
              <small className="is-success" role="status">
                Recorded V3 artifact version <code>{recordState.artifactVersionId}</code>
                {recordState.replayed ? " · idempotent replay" : ""}
              </small>
            ) : recordState.status === "error" ? (
              <small className="is-error" role="alert">Recording failed: {recordState.message}</small>
            ) : (
              <small>Records the current native Loom content state as a canonical V3 artifact; the local Pattern and its steps remain browser-local.</small>
            )}
          </div>
        </div>
      ) : (
        <div aria-disabled={Boolean(authoringReadOnlyReason)} className="loom-authoring-build" data-testid="loom-authoring-build" inert={authoringReadOnlyReason ? true : undefined}>
          <LoomAirshowPresetBrowser
            disabled={Boolean(authoringReadOnlyReason)}
            document={document}
            onCommand={onCommand}
            onSelect={onSelect}
          />
          <div className="loom-primitive-add">
            <select aria-label="Loom primitive type" onChange={(event) => setPrimitive(event.target.value as typeof primitive)} value={primitive}>
              {LOOM_PRIMITIVE_CATALOG.map((entry) => <option key={entry.id} value={entry.id}>{entry.label}</option>)}
            </select>
            <button data-testid="loom-add-primitive" onClick={addPrimitive} type="button">Add primitive</button>
          </div>
          <LoomPatternPlaybookPanel document={document} />
          {hideProductTree ? null : <LoomProductTree document={document} onSelect={onSelect} selectedNodeId={effectiveSelectedNodeId} />}
          <details className="loom-advanced-construction" data-testid="loom-advanced-construction">
            <summary>Advanced construction</summary>
            <p>Primitive, transform and Boolean evaluator graph. This is not the aircraft ownership tree.</p>
            <LoomPatternTree document={document} onSelect={onSelect} selectedNodeId={effectiveSelectedNodeId} />
          </details>
          {selected ? (
            <div
              className="loom-node-inspector"
              data-node-id={selected.id}
              data-revision={document.revision}
              data-testid="loom-node-inspector"
            >
              <div><span>Active part</span><strong>{selected.label}</strong><code>{selected.id}</code></div>
              {hideTaskDuplicateControls ? null : <LoomActivePartActions document={document} node={selected} onCommand={onCommand} onSelect={onSelect} />}
              {hideTaskDuplicateControls ? null : <LoomControlSurfacePanel />}
              {hideTaskDuplicateControls ? null : <label className="loom-authoring-field">
                <span>Name</span>
                <input defaultValue={selected.label} key={`${selected.id}:${selected.label}`} onBlur={(event) => {
                  if (event.target.value.trim() && event.target.value !== selected.label) submit({
                    ...commandBase(document, "rename"), kind: "node.rename", nodeId: selected.id, label: event.target.value,
                  });
                }} />
              </label>}
              {selected.type === "primitive" && selected.definition.primitive === "wing" ? (
                <div
                  className="loom-mirror-relation"
                  data-mirror-entity-count={linkedMirrorAttributes.length}
                  data-partner-node-id={mirrorAttribute?.partnerNodeId}
                  data-source-node-id={selected.id}
                  data-testid="loom-wing-mirror-attribute"
                >
                  <span>Wing attribute</span><strong>Mirror</strong>
                  <label><span>Enabled</span><input
                    aria-label="Mirror wing"
                    checked={Boolean(mirrorAttribute)}
                    data-testid="loom-wing-mirror-enabled"
                    disabled={linkedMirrorAttributes.length > 1 || (!mirrorAttribute && !mirrorAttachment)}
                    onChange={(event) => setWingMirrorEnabled(event.target.checked)}
                    type="checkbox"
                  /></label>
                  <label><span>Plane</span><select
                    aria-label="Wing mirror plane"
                    data-testid="loom-wing-mirror-axis"
                    disabled={linkedMirrorAttributes.length > 1 || (!mirrorAttribute && !mirrorAttachment)}
                    onChange={(event) => { const axis = event.target.value as LoomAxis; setMirrorAxis(axis); updateWingMirror(axis, mirrorPlaneOffsetM); }}
                    value={mirrorAxis}
                  ><option value="x">YZ · flip X</option><option value="y">XZ · flip Y</option><option value="z">XY · flip Z</option></select></label>
                  <label><span>World coordinate (m)</span><input
                    aria-label="Wing mirror world plane location (m)"
                    data-testid="loom-wing-mirror-plane-offset"
                    disabled={linkedMirrorAttributes.length > 1 || (!mirrorAttribute && !mirrorAttachment)}
                    onBlur={() => updateWingMirror(mirrorAxis, mirrorPlaneOffsetM)}
                    onChange={(event) => setMirrorPlaneOffsetM(Number(event.target.value))}
                    step="0.1" type="number" value={mirrorPlaneOffsetM}
                  /></label>
                  <small>One authored Wing; this attribute records a distinct reflected surface entity in the Pattern graph.</small>
                  {mirrorAttribute ? <code data-testid="loom-wing-mirror-lineage">{mirrorAttribute.sourceNodeId} → {mirrorAttribute.partnerNodeId}</code> : null}
                  {linkedMirrorAttributes.length > 1 ? <small className="is-gated">This legacy Wing has multiple mirror entities; edit them through the command API before using the single-attribute control.</small> : null}
                  {mirrorPartner?.type === "transform" && mirrorPartner.operation.transform === "mirror" && loomMirrorAeroGate(mirrorPartner.operation) ? <small className="is-gated" data-testid="loom-mirror-aero-gate">{loomMirrorAeroGate(mirrorPartner.operation)}</small> : null}
                </div>
              ) : null}
              {selectedDerivedMirror ? (
                <div className="loom-mirror-relation" data-source-node-id={selectedDerivedMirror.sourceNodeId} data-testid="loom-derived-mirror-relation">
                  <span>Derived entity</span><strong>Mirrored surface</strong>
                  <code>{selectedDerivedMirror.sourceNodeId} → {selectedDerivedMirror.partnerNodeId}</code>
                  <small>This is a distinct reflected entity. Its shape is authored by the source; its reflection plane is editable here.</small>
                  <button onClick={() => onSelect(selectedDerivedMirror.sourceNodeId)} type="button">Open authored source</button>
                </div>
              ) : null}
              {pinnedControlIds.length > 0 ? (
                <div className="loom-authoring-intent" data-testid="loom-authoring-intent">
                  Assistant pinned {pinnedControlIds.length} schema control{pinnedControlIds.length === 1 ? "" : "s"}
                </div>
              ) : null}
              {hideTaskDuplicateControls ? null : <div data-testid={selected.type === "transform" ? `loom-transform-controls-${selected.id}` : undefined}>
                <LoomAutoParameterControls
                  document={document}
                  nodeId={selected.id}
                  onCommand={onCommand}
                  onPreviewCancel={onPreviewCancel}
                  onPreviewCommand={onPreviewCommand}
                  pinnedControlIds={pinnedControlIds}
                />
              </div>}
              {hideTaskDuplicateControls ? null : <LoomExplicitSurfaceCage
                document={document}
                nodeId={selected.id}
                onCommand={onCommand}
                onPreviewCancel={onPreviewCancel}
                onPreviewCommand={onPreviewCommand}
              />}
              {selected.type === "primitive" ? null : (
                <details className="loom-node-json"><summary>Node definition</summary><pre>{JSON.stringify(selected, null, 2)}</pre></details>
              )}
              {selected.type === "primitive" ? (() => {
                const entry = LOOM_PRIMITIVE_CATALOG.find((candidate) => candidate.id === selected.definition.primitive);
                return entry ? <div className="loom-capability-receipt">
                  <span>Implicit: {entry.capability.implicitPredicate}</span>
                  <span>Explicit: {entry.capability.explicitSurface}</span>
                  <span>Hybrid: {entry.capability.hybridExtraction}</span>
                </div> : null;
              })() : null}
            </div>
          ) : null}
          <div className="pg-definition-note">
            <strong>Definition preview ≠ extracted surface</strong>
            <span>Edits update the operand preview immediately. Loom must rebuild the conforming mesh, seam, derivatives and quality receipt before downstream aero is enabled.</span>
          </div>
        </div>
      )}
    </>
  );
}
